/**
 * <Snackbar> — l'accusé de réception non bloquant (molecule).
 *
 * « C'est enregistré » : un bandeau ancré en bas, qui se retire tout seul et n'attend aucune
 * réponse. Il remplace le `<ConfirmDialog>` détourné en accusé (dette notée dans
 * `ports/TRANSITION.md`) : un dialogue oui/non pour dire « c'est fait » demande un clic pour rien.
 *
 * Il ne bloque pas : le portail est traversé par les gestes (`box-none`), il n'y a pas de voile,
 * et la page dessous reste utilisable — c'est pour ça qu'il n'utilise pas `<Overlay>`, qui peint
 * toujours son scrim.
 *
 * Motion de la marque (chap. 13, `theme.motion`) : fondu + 12 px, la courbe du livre, `standard` (250 ms) à
 * l'entrée, `micro` (150 ms) à la sortie ; `entree` (400 ms) est réservé à l'entrée d'une page. Quand le système demande moins d'animation
 * (`prefers-reduced-motion` sur le web, Réduire les animations sur mobile), il apparaît sans
 * transition — le livre veut « le résultat arrive », pas le mouvement pour lui-même.
 */
import React, { useCallback, useEffect, useRef, useState } from "react"
import { Animated, Easing, Platform, View } from "react-native"
import { Portal } from "react-native-paper"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { useReduceMotion } from "../../hooks/useReduceMotion"
import { useScreenReader } from "../../hooks/useScreenReader"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { Row } from "../../atoms/containers/Row"
import { Text } from "../../atoms/typography/Text"
import { shadowStyle } from "../../utils/shadow"
import { snackbarAutoHideMs, useAutoHide } from "./useAutoHide"

export type SnackbarTone = "success" | "danger" | "info"

/** Décalage d'entrée : le bandeau monte de 12 px en apparaissant. */
const RISE = 12
/** Combien de temps un accusé reste à l'écran par défaut. */
const DEFAULT_AUTO_HIDE_MS = 4000

const TONE: Record<SnackbarTone, { icon: IconName; surface: SemanticColorKey; ink: SemanticColorKey }> = {
    success: { icon: "check-circle", surface: "successLight", ink: "success-strong" },
    danger: { icon: "exclamation-circle", surface: "dangerLight", ink: "danger-strong" },
    /** Une nouvelle qui ne félicite ni n'alerte : le tuteur affiche une activité pendant le live. */
    info: { icon: "tasks", surface: "infoLight", ink: "info" },
}

export type SnackbarProps = {
    visible: boolean
    message: string
    /** `success` (défaut) pour « c'est fait », `danger` pour « ça n'est pas passé », `info` pour une nouvelle. */
    tone?: SnackbarTone
    /** Le glyphe de tête, quand le ton n'est pas le bon dessin (le micro coupé d'un live). Défaut : celui du ton. */
    iconName?: IconName
    /**
     * Retrait automatique, en ms. `0` le garde jusqu'au geste de fermeture. Défaut 4 000. Avec `action`,
     * il attend le geste quand un lecteur d'écran est actif.
     */
    autoHideMs?: number
    onDismiss: () => void
    /**
     * Un geste qui suit la nouvelle (« Voir ») : un bouton texte avant la croix. Le toucher fait le geste
     * puis retire le bandeau. Absent : la croix seule.
     */
    action?: { label: string; onPress: () => void }
    /** Place à réserver sous le bandeau — la hauteur de la barre d'onglets, quand il y en a une. */
    bottomOffset?: number
    testID?: string
}

export function Snackbar(props: SnackbarProps) {
    const {
        visible,
        message,
        tone = "success",
        iconName,
        autoHideMs = DEFAULT_AUTO_HIDE_MS,
        onDismiss,
        bottomOffset = 0,
        action,
        testID,
    } = props

    const insets = useSafeAreaInsets()
    const [mounted, setMounted] = useState(visible)
    const reduceMotion = useReduceMotion()
    const { motion } = useUnistyles().theme
    const progress = useRef(new Animated.Value(visible ? 1 : 0)).current

    const animate = useCallback(
        (to: number, done?: () => void) => {
            Animated.timing(progress, {
                toValue: to,
                duration: reduceMotion ? 0 : to === 1 ? motion.standard : motion.micro,
                easing: Easing.bezier(...motion.easing),
                // react-native-web n'a pas de pilote natif ; y prétendre ne fait qu'avertir.
                useNativeDriver: Platform.OS !== "web",
            }).start(done)
        },
        [progress, reduceMotion, motion]
    )

    useEffect(() => {
        if (visible) {
            setMounted(true)
            animate(1)
            return
        }
        animate(0, () => setMounted(false))
    }, [visible, animate])

    // Porteur d'action, il attend le geste quand un lecteur d'écran est actif.
    const screenReader = useScreenReader()
    useAutoHide(
        visible,
        snackbarAutoHideMs(autoHideMs, { hasAction: action != null, screenReader }),
        onDismiss,
        message
    )

    if (!mounted) return null

    const { surface, ink } = TONE[tone]
    const icon = iconName ?? TONE[tone].icon

    return (
        <Portal>
            <View style={styles.anchor(insets.bottom + bottomOffset)} pointerEvents="box-none" testID={testID}>
                <Animated.View
                    accessibilityRole="alert"
                    accessibilityLiveRegion="polite"
                    style={[
                        styles.bar(surface),
                        {
                            opacity: progress,
                            transform: [
                                { translateY: progress.interpolate({ inputRange: [0, 1], outputRange: [RISE, 0] }) },
                            ],
                        },
                    ]}
                >
                    <Row gap="3" align="center">
                        <Icon name={icon} size={20} color={ink} />
                        <Text size="text-base" weight="semibold" color={ink} style={styles.message}>
                            {message}
                        </Text>
                        {action != null && (
                            <TextButton
                                label={action.label}
                                size="sm"
                                color={ink}
                                onPress={() => {
                                    action.onPress()
                                    onDismiss()
                                }}
                            />
                        )}
                        <IconButton
                            iconName="times"
                            appearance="transparent"
                            iconColor={ink}
                            size={28}
                            iconSize={16}
                            onPress={onDismiss}
                            accessibilityLabel="Fermer"
                        />
                    </Row>
                </Animated.View>
            </View>
        </Portal>
    )
}

const styles = StyleSheet.create((theme) => ({
    anchor: (bottom: number) => ({
        position: "absolute" as const,
        left: 0,
        right: 0,
        bottom: bottom + theme.spacing["3"],
        alignItems: "center" as const,
        paddingHorizontal: theme.spacing["4"],
        zIndex: 1100,
        elevation: 1100,
    }),
    bar: (surface: SemanticColorKey) => ({
        width: "100%" as const,
        maxWidth: 520,
        paddingVertical: theme.spacing["2"],
        paddingLeft: theme.spacing["4"],
        paddingRight: theme.spacing["2"],
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors[surface],
        borderWidth: 1,
        borderColor: theme.colors.border,
        ...shadowStyle(theme, "floating"),
    }),
    message: { flexShrink: 1, flexGrow: 1 },
}))
